// Robust initialization system to prevent "£0.00 until refresh" issues
function initializeBrickCalculator(isRetry = false) {
  const calculator = document.getElementById('brick-calculator-wrapper');
  if (!calculator) {
    if (isRetry) console.warn('🧱 Brick calculator wrapper not found');
    return false;
  }

  // Validate ALL required elements exist before proceeding
  const requiredElements = {
    'thickness': '#brick-thickness',
    'shape': '#brick-shape',
    'width': '#brick-width',
    'height': '#brick-height',
    'quantity': '#brick-quantity',
    'priceDisplay': '#brick-price-display',
    'addToCartButton': '#brick-add-to-cart-button'
  };

  const missingElements = [];
  for (const [name, selector] of Object.entries(requiredElements)) {
    if (!calculator.querySelector(selector)) {
      missingElements.push(`${name} (${selector})`);
    }
  }

  if (missingElements.length > 0) {
    console.warn('🧱 Brick calculator missing elements:', missingElements);
    return false;
  }

  console.log('✅ Brick calculator: All required elements found, initializing...');
  markBrickCalculatorInitialized();
  return true;
}

// Retry logic with exponential backoff
function attemptBrickInitialization(attempt = 1, maxAttempts = 10) {
  if (initializeBrickCalculator(attempt > 1)) {
    console.log('✅ Brick calculator initialized successfully');
    startBrickCalculator();
  } else if (attempt < maxAttempts) {
    const delay = Math.min(100 * Math.pow(1.5, attempt), 2000); // Max 2 second delay
    if (attempt > 1) console.log(`⏳ Brick calculator retry ${attempt}/${maxAttempts} in ${delay}ms...`);
    setTimeout(() => attemptBrickInitialization(attempt + 1, maxAttempts), delay);
  } else {
    console.error('❌ Brick calculator failed to initialize after', maxAttempts, 'attempts');
  }
}

// Main calculator logic (extracted from DOMContentLoaded)
function startBrickCalculator() {
  const calculator = document.getElementById('brick-calculator-wrapper');
  if (!calculator) return;
  if (calculator.dataset.started === 'true') {
    console.warn('🧱 Brick calculator already started, skipping duplicate init');
    return;
  }
  calculator.dataset.started = 'true';

  // --- Configuration ---
  const THICKNESS_PRICES = {
    '15mm': 202,
    '20mm': 202,
    '25mm': 202,
    '30mm': 235
  };

  const SHAPE_PRICES = {
    'square-rectangular': 0,
    'single-corner-cut': 8,        // Applied silently (not shown in dropdown)
    'both-top-corners-cut': 8,     // Applied silently (not shown in dropdown)
    'custom-shaped': 8             // Applied silently (not shown in dropdown)
  };

  const BASE_PRICE = 8.20; // Fixed base price in formula

  // Pallet surcharge - any brick >=600mm ships on single pallet £53.05 ( £50+VAT minus checkout shipping)
  const PALLET_SURCHARGE = 53.05; // flat for >=600mm, one pallet per order

  // --- Get all calculator elements ---
  const thicknessSelect = calculator.querySelector('#brick-thickness');
  const shapeSelect = calculator.querySelector('#brick-shape');
  const widthInput = calculator.querySelector('#brick-width');
  const heightInput = calculator.querySelector('#brick-height');
  const inputC = calculator.querySelector('#brick-input-c');
  const inputD = calculator.querySelector('#brick-input-d');
  const cornerDimensions = calculator.querySelector('#corner-dimensions');
  const svgPreview = calculator.querySelector('#brick-svg-preview');
  const svgContainer = calculator.querySelector('#brick-svg-container');
  const quantityInput = calculator.querySelector('#brick-quantity');
  const fileUploadSection = calculator.querySelector('#file-upload-section');

  // Display elements
  const priceDisplay = calculator.querySelector('#brick-price-display');
  const hiddenPriceInput = calculator.querySelector('#brick-price-property');

  // File upload elements
  const fileInput = calculator.querySelector('#drawing-upload');
  const uploadButton = calculator.querySelector('#upload-button');
  const uploadText = calculator.querySelector('#upload-text');
  const uploadStatus = calculator.querySelector('#upload-status');
  const uploadedFileInfo = calculator.querySelector('#uploaded-file-info');
  const fileName = calculator.querySelector('#file-name');
  const removeFileButton = calculator.querySelector('#remove-file');
  const uploadedFileUrl = calculator.querySelector('#uploaded-file-url');

  let itemPrice = 0; // Store single item price

  // --- Generate SVG preview for corner cuts (FIXED) ---
  function updateBrickSVG() {
    const shape = shapeSelect.value;
    const width = parseFloat(widthInput.value) || 0;
    const height = parseFloat(heightInput.value) || 0;
    const c = parseFloat(inputC.value) || 0;
    const d = parseFloat(inputD.value) || 0;

    if (!svgContainer) return;

    let svgHTML = '';

    if (shape === 'single-corner-cut') {
      // Single corner cut - C is height UP FROM BOTTOM to cut-off, D is top flat width
      // Shape: flat top for D width, then diagonal cut to C height from bottom
      const cutStartX = 40 + (d * 120 / width); // Where diagonal cut starts (based on D)
      const cutStartY = 150 - (c * 100 / height); // Where diagonal ends (C measured UP from bottom)

      svgHTML = `
        <svg viewBox="0 0 300 200" style="width: 100%; max-width: 350px; height: auto;">
          <rect width="300" height="200" fill="#f8f9fa"/>
          
          <!-- Single corner cut shape: D = flat width at top, C = height UP from bottom to where cut starts -->
          <path d="M 40 150 L 40 50 L ${cutStartX} 50 L 160 ${cutStartY} L 160 150 Z" 
                fill="rgba(173, 216, 230, 0.3)" stroke="#007bff" stroke-width="2"/>
          
          <!-- Dimension lines -->
          <line x1="40" y1="170" x2="160" y2="170" stroke="#666" stroke-width="1"/>
          <line x1="220" y1="50" x2="220" y2="150" stroke="#666" stroke-width="1"/>
          
          <!-- Labels (positioned away from shape) -->
          <text x="100" y="185" text-anchor="middle" font-size="12" font-weight="bold" fill="#007bff">A (Overall Width) = ${width}mm</text>
          <text x="240" y="105" text-anchor="middle" font-size="12" font-weight="bold" fill="#007bff" transform="rotate(90, 240, 105)">B (Overall Height) = ${height}mm</text>
          ${c > 0 ? `<text x="180" y="${Math.max(cutStartY, 80)}" text-anchor="middle" font-size="11" fill="#dc3545" transform="rotate(90, 180, ${Math.max(cutStartY, 80)})">C (Height to Cut-off) = ${c}mm</text>` : ''}
          ${d > 0 ? `<text x="${Math.max(cutStartX / 2 + 40, 70)}" y="38" text-anchor="middle" font-size="11" fill="#28a745">D (Top Flat Width) = ${d}mm</text>` : ''}
        </svg>
      `;
    } else if (shape === 'both-top-corners-cut') {
      // Both top corners cut - C is height UP FROM BOTTOM to cut-off, D is top flat width (center section)
      // Shape: flat top for D width in center, diagonal cuts on both sides to C height from bottom
      const leftCutEndX = 75 + ((width - d) / 2) * 150 / width; // Left diagonal ends
      const rightCutStartX = 225 - ((width - d) / 2) * 150 / width; // Right diagonal starts
      const cutStartY = 150 - (c * 100 / height); // Where diagonals end (C measured UP from bottom)

      svgHTML = `
        <svg viewBox="0 0 300 200" style="width: 100%; max-width: 350px; height: auto;">
          <rect width="300" height="200" fill="#f8f9fa"/>
          
          <!-- Both corners cut shape: D = flat width at top center, C = height UP from bottom to where cuts start -->
          <path d="M 75 150 L 75 ${cutStartY} L ${leftCutEndX} 50 L ${rightCutStartX} 50 L 225 ${cutStartY} L 225 150 Z" 
                fill="rgba(173, 216, 230, 0.3)" stroke="#007bff" stroke-width="2"/>
          
          <!-- Dimension lines -->
          <line x1="75" y1="170" x2="225" y2="170" stroke="#666" stroke-width="1"/>
          <line x1="245" y1="50" x2="245" y2="150" stroke="#666" stroke-width="1"/>
          
          <!-- Labels (positioned away from shape) -->
          <text x="150" y="185" text-anchor="middle" font-size="12" font-weight="bold" fill="#007bff">A (Overall Width) = ${width}mm</text>
          <text x="265" y="105" text-anchor="middle" font-size="12" font-weight="bold" fill="#007bff" transform="rotate(90, 265, 105)">B (Overall Height) = ${height}mm</text>
          ${c > 0 ? `<text x="50" y="${Math.max(cutStartY, 80)}" text-anchor="middle" font-size="11" fill="#dc3545" transform="rotate(90, 50, ${Math.max(cutStartY, 80)})">C (Height to Cut-off) = ${c}mm</text>` : ''}
          ${d > 0 ? `<text x="150" y="38" text-anchor="middle" font-size="11" fill="#28a745">D (Top Flat Width) = ${d}mm</text>` : ''}
        </svg>
      `;
    } else {
      // Clear for other shapes
      svgContainer.innerHTML = '';
      return;
    }

    svgContainer.innerHTML = svgHTML;
  }

  // --- Show/hide sections based on shape ---
  function updateShapeOptions() {
    const selectedShape = shapeSelect.value;

    // Show file upload only for custom-shaped
    if (fileUploadSection) {
      fileUploadSection.style.display = selectedShape === 'custom-shaped' ? 'block' : 'none';
    }

    // Show corner dimensions for corner cut shapes
    if (cornerDimensions) {
      const isCornerCut = selectedShape === 'single-corner-cut' || selectedShape === 'both-top-corners-cut';
      cornerDimensions.style.display = isCornerCut ? 'flex' : 'none';
    }

    // Update SVG preview for corner cuts
    if (selectedShape === 'single-corner-cut' || selectedShape === 'both-top-corners-cut') {
      updateBrickSVG();
      if (svgPreview) svgPreview.style.display = 'block';
    } else {
      if (svgPreview) svgPreview.style.display = 'none';
    }
  }

  // --- Main calculation function ---
  function updateCalculator() {
    const width = parseFloat(widthInput.value) || 0;
    const height = parseFloat(heightInput.value) || 0;
    const quantity = Math.max(1, parseInt(quantityInput.value) || 1);
    const thickness = thicknessSelect.value;
    const shape = shapeSelect.value;

    // Update shape options
    updateShapeOptions();

    // Calculate using your exact formula:
    // ((((((Enter brick width (mm) * Enter brick height (mm)) / 1000000) * thickness of brick) * quantity ) + 8.20) + My brick is )

    if (width > 0 && height > 0) {
      // Step 1: (width * height) / 1000000
      const areaInSquareMeters = (width * height) / 1000000;

      // Step 2: area * thickness price
      const thicknessPrice = THICKNESS_PRICES[thickness] || 0;
      const areaTimesThickness = areaInSquareMeters * thicknessPrice;

      // Step 3: multiply by quantity
      const quantityTotal = areaTimesThickness * quantity;

      // Step 4: add base price (8.20)
      const withBasePrice = quantityTotal + BASE_PRICE;

      // Step 5: add shape price
      const shapePrice = SHAPE_PRICES[shape] || 0;
      let brickPrice = withBasePrice + shapePrice;

      // Step 6: Calculate shipping surcharge based on size
      let shippingSurcharge = 0;

      // Determine the largest dimension in mm for shipping calculation
      const largestDimension = Math.max(width, height);

      // Apply pallet surcharge - flat £53.05 for any dimension >=600mm (one pallet per order)
      if (largestDimension >= 600) {
        shippingSurcharge = PALLET_SURCHARGE;
      }

      // Add shipping surcharge to brick price
      const totalPrice = brickPrice + shippingSurcharge;

      // Round the per-piece price to pennies once so display, pool variant
      // price and penny-fallback quantity all agree exactly (the pallet/base
      // costs are order-level, so per-piece division needs the round).
      itemPrice = Math.round((totalPrice / quantity) * 100) / 100;
    } else {
      itemPrice = 0;
    }

    // Update display (show total price for all bricks)
    const totalDisplayPrice = itemPrice * quantity;
    if (priceDisplay) {
      priceDisplay.textContent = `£${totalDisplayPrice.toFixed(2)}`;
    }

    if (hiddenPriceInput) {
      hiddenPriceInput.value = `£${itemPrice.toFixed(2)}`;
    }
  }

  // --- Add to Cart Logic (using same variant pool system) ---
  const addToCartButton = calculator.querySelector('#brick-add-to-cart-button');
  const addToCartText = calculator.querySelector('#brick-add-to-cart-text');

  // Initialize enhanced variant pool system 
  const simplePool = window.SimpleVariantPool ? new window.SimpleVariantPool() : null;
  const draftOrderHandler = window.DraftOrderHandler ? new window.DraftOrderHandler() : null;

  // Configure pool for brick products
  if (simplePool) {
    simplePool.setProductType('brick');
    console.log('🧱 Brick calculator: Enhanced variant pool initialized with fallback protection');
  }

  // Brick pool variant IDs (created by script)
  const BRICK_POOL_VARIANTS = [
    '56553509323139', // Brick Pool #1
    '56553509355907', // Brick Pool #2
    '56553509388675', // Brick Pool #3
    '56553509421443', // Brick Pool #4
    '56553509519747', // Brick Pool #5
    '56553509552515', // Brick Pool #6
    '56553509585283', // Brick Pool #7
    '56553509618051', // Brick Pool #8
    '56553509650819', // Brick Pool #9
    '56553509683587', // Brick Pool #10
    '56553509749123', // Brick Pool #11
    '56553509781891', // Brick Pool #12
    '56553509814659', // Brick Pool #13
    '56553509847427', // Brick Pool #14
    '56553509880195', // Brick Pool #15
    '56553509912963', // Brick Pool #16
    '56553510011267', // Brick Pool #17
    '56553510044035', // Brick Pool #18
    '56553510076803', // Brick Pool #19
    '56553510109571', // Brick Pool #20
    '56553510142339', // Brick Pool #21
    '56553510437251', // Brick Pool #22
    '56553510502787', // Brick Pool #23
    '56553510535555', // Brick Pool #24
    '56553510568323', // Brick Pool #25
    '56553510601091', // Brick Pool #26
    '56553510666627', // Brick Pool #27
    '56553510699395', // Brick Pool #28
    '56553510732163', // Brick Pool #29
    '56553510830467', // Brick Pool #30
    '56553510863235', // Brick Pool #31
    '56553510994307', // Brick Pool #32
    '56553511027075', // Brick Pool #33
    '56553511059843', // Brick Pool #34
    '56553511092611', // Brick Pool #35
    '56553511125379', // Brick Pool #36
    '56553511190915', // Brick Pool #37
    '56553511223683', // Brick Pool #38
    '56553511256451', // Brick Pool #39
    '56553511289219', // Brick Pool #40
    '56553511321987', // Brick Pool #42 (Pool #41 deleted)
    '56553511387523', // Brick Pool #43
    '56553511420291', // Brick Pool #44
    '56553511453059', // Brick Pool #45
    '56553511485827', // Brick Pool #46
    '56553511518595', // Brick Pool #47
    '56553511551363', // Brick Pool #48
    '56553511584131', // Brick Pool #49
    '56553511715203'  // Brick Pool #50
  ];

  let lastClickTime = 0;
  addToCartButton.addEventListener('click', async () => {
    // IMMEDIATE PREVENTION: Reject clicks within 500ms of each other (Timestamp Debounce)
    const now = Date.now();
    if (now - lastClickTime < 500) {
      console.warn('⚡ Quick-fire click detected, ignoring');
      return;
    }
    lastClickTime = now;

    // IMMEDIATE PREVENTION: Disable button at the very first millisecond to prevent concurrent clicks
    addToCartButton.disabled = true;

    const userQuantity = Math.max(1, parseInt(quantityInput.value) || 1);
    const width = parseFloat(widthInput.value) || 0;
    const height = parseFloat(heightInput.value) || 0;
    const thickness = thicknessSelect.value;
    const shape = shapeSelect.value;
    const thicknessText = thicknessSelect.options[thicknessSelect.selectedIndex].text;
    const shapeText = shapeSelect.options[shapeSelect.selectedIndex].text;

    // Prepare helper dimensions for corner cut shapes (parsed early for validation)
    const c = parseFloat(inputC.value) || 0;
    const d = parseFloat(inputD.value) || 0;

    // Validate inputs
    if (width <= 0 || height <= 0) {
      alert('Please enter valid width and height dimensions.');
      addToCartButton.disabled = false;
      return;
    }

    // Corner-cut shapes need C and D (same rules as the glass calculator)
    if (shape === 'single-corner-cut' || shape === 'both-top-corners-cut') {
      if (!c || c <= 0) {
        alert('Please enter a Height to Cut-off (C) greater than 0.');
        addToCartButton.disabled = false;
        return;
      }
      if (c > height) {
        alert('Cut-off height (C) cannot exceed the overall height.');
        addToCartButton.disabled = false;
        return;
      }
      if (!d || d <= 0) {
        alert('Please enter a Top Flat Width (D) greater than 0.');
        addToCartButton.disabled = false;
        return;
      }
      if (d > width) {
        alert('Top flat width (D) cannot exceed the overall width.');
        addToCartButton.disabled = false;
        return;
      }
    }

    let properties = {
      'Product Type': 'Cut to Size Bricks',
      'Thickness': thicknessText,
      'Brick Shape': shapeText,
      'Width': `${width}mm`,
      'Height': `${height}mm`,
      'Dimensions': `${width}mm × ${height}mm`
    };

    // (c and d are parsed above, before validation)

    // Handle corner cut shapes - generate spec image using Cloudinary templates (same as glass calculator)
    if (shape === 'single-corner-cut' || shape === 'both-top-corners-cut') {
      properties['Corner Cut C'] = `${c}mm`;
      properties['Corner Cut D'] = `${d}mm`;

      // Generate spec image URL using SpecImageGenerator (no upload needed)
      if (window.SpecImageGenerator) {
        try {
          const specGen = new window.SpecImageGenerator();
          const specData = specGen.generateSpecUrl({
            productType: 'brick',
            shape: shape,
            width: width,
            height: height,
            diameter: null,
            thickness: thickness,
            units: 'mm',
            inputC: c,
            inputD: d
          });

          if (specData.specUrl) {
            properties['Spec Image'] = specData.specUrl;
            console.log('✅ Brick corner cut spec image generated:', specData.specUrl);
          }
        } catch (err) {
          console.warn('⚠️ Spec image generation failed (non-critical):', err);
        }
      }
    }

    // Handle custom-shaped file upload or complex shape spec images
    if (shape === 'custom-shaped' && fileInput.files && fileInput.files[0]) {
      properties['Drawing Uploaded'] = 'Yes';
    }

    // Generate supplier spec image and code (skip if the corner-cut branch above
    // already generated one - this used to overwrite it with C=0/D=0)
    const specGenerator = window.SpecImageGenerator ? new window.SpecImageGenerator() : null;
    if (specGenerator && !properties['Spec Image']) {
      const specData = specGenerator.generateSpecUrl({
        productType: 'brick',
        shape: shape,
        width: width,
        height: height,
        diameter: 0,
        thickness: thickness,
        units: 'mm',
        inputC: c,
        inputD: d
      });

      // Only add spec image for complex shapes that need visual clarification
      if (specData.specUrl) {
        properties['Spec Image'] = specData.specUrl;
      }
    }

    // Internal properties for system use
    properties['_Quantity'] = userQuantity.toString();
    properties['_Added At'] = Date.now().toString();
    properties['_Internal_Type'] = 'brick';
    properties['_Internal_Thickness'] = thickness;
    properties['_Internal_Shape'] = shape;
    properties['_Width'] = width.toString();
    properties['_Height'] = height.toString();

    // Fixed fallback function using the scaled quantity method (proven reliable)
    const fallbackToOriginalCart = () => {
      if (!Number.isFinite(itemPrice) || itemPrice <= 0.01) {
        console.error('Invalid price detected, blocking fallback:', itemPrice);
        addToCartButton.disabled = false;
        addToCartText.textContent = 'Add to Cart';
        alert('Pricing error detected. Please recalculate and try again.');
        return;
      }
      console.log('🔄 Triggering brick calculator FALLBACK (price unit scaling)');

      // Standard fallback variant ID for bricks
      const BRICK_PRICE_UNIT_VARIANT_ID = '56535863787907';
      const quantityForCart = Math.round(itemPrice * 100) * userQuantity;

      // Add ground truth to fallback too for monitoring
      properties['_Expected Price'] = Number(itemPrice).toFixed(2);
      properties['_Product Title'] = 'Custom Cut Brick';
      properties['_Fallback Applied'] = 'True';
      properties['_Added At'] = Date.now().toString();

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: BRICK_PRICE_UNIT_VARIANT_ID,
          quantity: quantityForCart,
          properties: properties
        })
      })
        .then(response => {
          if (!response.ok) throw new Error('Fallback add failed');
          return response.json();
        })
        .then(async (result) => {
          console.log('✅ Brick fallback add succeeded, verifying quantity...');
          const expectedPrice = Number(itemPrice).toFixed(2);

          // The add response is authoritative and immune to cart-read lag.
          let addConfirmed = false;
          try {
            const lines = result && Array.isArray(result.items) ? result.items : (result ? [result] : []);
            const line = lines.find(it =>
              String(it.variant_id || it.id || '') === String(BRICK_PRICE_UNIT_VARIANT_ID) &&
              it.properties && it.properties['_Expected Price'] === expectedPrice);
            if (line) {
              const lineQty = parseInt(line.properties._Quantity || userQuantity.toString(), 10) || userQuantity;
              const lineExpectedQty = Math.round(itemPrice * 100) * lineQty;
              if (Number(line.quantity) === lineExpectedQty) addConfirmed = true;
            }
            console.log('Brick fallback add response check', { confirmed: addConfirmed, lineQuantity: line ? line.quantity : null });
          } catch (parseErr) {
            console.warn('Could not parse brick fallback add response', parseErr);
          }

          if (!addConfirmed) {
            try {
              let verifyCart = null;
              let added = null;
              const expectedQtyFor = (it) => {
                const cartQty = parseInt(it.properties._Quantity || userQuantity.toString(), 10) || userQuantity;
                return Math.round(itemPrice * 100) * cartQty;
              };
              // Storefront cart reads can lag the add; retry before failing.
              for (let attempt = 0; attempt < 5; attempt++) {
                const verifyRes = await fetch(`/cart.js?_cb=${Date.now()}_${attempt}`, { cache: 'no-store' });
                if (!verifyRes.ok) throw new Error('Cart verify failed');
                verifyCart = await verifyRes.json();
                // Same first-match hazard as glass: two brick specs at one price
                // would validate/remove the wrong line. Exact-match first.
                const candidates = verifyCart.items.filter(it =>
                  it.variant_id.toString() === BRICK_PRICE_UNIT_VARIANT_ID &&
                  it.properties && it.properties['_Expected Price'] === expectedPrice);
                added = candidates.find(it => it.quantity === expectedQtyFor(it)) ||
                  candidates[candidates.length - 1] || null;
                if (added && added.quantity === expectedQtyFor(added)) break;
                if (attempt < 4) await new Promise(resolve => setTimeout(resolve, 400));
              }
              if (!added) throw new Error('FALLBACK_VERIFY_NOT_FOUND');
              const expectedQty = expectedQtyFor(added);
              if (added.quantity !== expectedQty) {
                console.error('🚨 BRICK FALLBACK VERIFICATION FAILED', { expectedQty, actualQty: added.quantity });
                const lineIndex = verifyCart.items.indexOf(added) + 1;
                await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ line: lineIndex, quantity: 0 }) });
                throw new Error(`FALLBACK_QUANTITY_MISMATCH: expected ${expectedQty} got ${added.quantity}`);
              }
              console.log('✅ Brick fallback verification passed', { expectedQty });
            } catch (verifyErr) {
              console.error('❌ Brick fallback verification error:', verifyErr);
              if (verifyErr.message.includes('FALLBACK_QUANTITY_MISMATCH')) {
                addToCartText.textContent = 'Pricing error - please contact support';
                addToCartButton.disabled = false;
                alert('Brick fallback verification failed - quantity not accepted (inventory cap). Please contact support.');
                return;
              }
              console.warn('⚠️ Brick cart read did not confirm the line; proceeding (add response was accepted)', verifyErr);
            }
          } else {
            console.log('✅ Brick fallback add response confirmed quantity; skipping cart read verification');
          }
          console.log('✅ Brick fallback successful');
          window.location.href = '/cart';
        })
        .catch(error => {
          console.error('❌ Brick fallback error:', error);
          addToCartText.textContent = 'Error - Try Again';
          addToCartButton.disabled = false;
          alert('Unable to add brick to cart. Please try again or contact support.');
        });
    };

    // Update button state with consistent messaging like glass calculator
    addToCartText.textContent = 'Creating Your Order...';
    addToCartButton.disabled = true;

    // Add success callback for better user feedback
    const onSuccess = () => {
      addToCartText.textContent = 'Added!';
      setTimeout(() => {
        window.location.href = '/cart';
      }, 500); // Standard delay from glass calculator
    };

    const onError = (error) => {
      console.error('❌ Add to cart error:', error);
      addToCartText.textContent = 'Error!';
      setTimeout(() => {
        addToCartText.textContent = 'Add to Cart';
        addToCartButton.disabled = false;
      }, 2000); // Match glass calculator timeout
    };

    // Check if file is present AND shape is custom-shaped (multipart upload only for actual files)
    const hasFile = shape === 'custom-shaped' && fileInput && fileInput.files && fileInput.files.length > 0;

    if (hasFile && simplePool) {
      console.log('🧱 Using standard multipart form upload for custom-shaped brick with file...');

      try {
        addToCartText.textContent = 'Updating Product...';

        // Step 1: Find available variant and update details
        const variantId = await simplePool.findAvailableVariant();
        const title = 'Custom Cut Brick';
        const sku = simplePool.generateSKU({
          productType: 'brick',
          shape: shape,
          width: width,
          height: height,
          diameter: null,
          thickness: thickness,
          units: 'mm',
          inputC: c,
          inputD: d
        });

        await simplePool.updateVariantDetails(variantId, { title, price: itemPrice, sku });

        // Step 2: Build multipart form for /cart/add (required for file uploads)
        const form = document.createElement('form');
        form.action = '/cart/add';
        form.method = 'POST';
        form.enctype = 'multipart/form-data';
        form.style.display = 'none';

        const idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        idInput.value = variantId;
        form.appendChild(idInput);

        const qtyInput = document.createElement('input');
        qtyInput.type = 'hidden';
        qtyInput.name = 'quantity';
        qtyInput.value = userQuantity.toString();
        form.appendChild(qtyInput);

        // Add properties (except internal ones starting with _)
        // _-prefixed audit/recovery markers pass through (hidden storefront-side).
        const SGR_KEEP = new Set(['_View Drawing', '_Spec Image', '_Drawing',
          '_Expected SKU', '_Expected Price', '_Product Title',
          '_ClientPatchVersion', '_ValidationSource', '_AddPath',
          '_Quantity', '_Added At', '_Internal_Shape', '_Internal_Thickness', '_Internal_Type',
          '_Width', '_Height']);
        for (const [key, value] of Object.entries(properties)) {
          if (key.startsWith('_') && !SGR_KEEP.has(key)) continue;
          if (key === 'Drawing URL') continue; // Skip the filename string, we're uploading actual file
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = value;
          form.appendChild(input);
        }

        // Move the actual file input to the form
        fileInput.name = 'properties[Drawing]';
        form.appendChild(fileInput);

        document.body.appendChild(form);
        console.log(`Submitting brick file: ${fileInput.files[0].name}. Waiting for propagation...`);

        // Wait for propagation before submitting (multipart can't be verified post-add easily)
        await new Promise(res => setTimeout(res, 2000));

        // Use native submit to bypass theme's AJAX cart interceptor
        setTimeout(() => {
          HTMLFormElement.prototype.submit.call(form);
        }, 100);
        return;

      } catch (e) {
        console.error('Multipart brick upload failed:', e);
        alert('Failed to upload your file. Please try again.');
        addToCartButton.disabled = false;
        addToCartText.textContent = 'Add to Cart';
        return;
      }
    }

    // Standard Order Path: Use consolidated simplePool.processOrder for full protection
    if (simplePool) {
      console.log('🧱 Using consolidated simplePool.processOrder for brick order');

      const calculatorData = {
        productType: 'brick',
        shape: shape,
        width: width,
        height: height,
        diameter: null,
        thickness: thickness,
        units: 'mm',
        inputC: c,
        inputD: d,
        itemPrice: itemPrice,
        quantity: userQuantity,
        properties: properties,
        shapeText: shapeText,
        thicknessText: thicknessText,
        supplierSpecUrl: properties['Spec Image'] || null
      };

      try {
        await simplePool.processOrder(calculatorData, fallbackToOriginalCart);
        // processOrder handles redirection
      } catch (error) {
        console.error('🔄 Brick order processing failed:', error);
        // Fallback is handled inside processOrder usually, but safe to be explicit
        if (error.message !== 'PRICE_MISMATCH_DETECTED') {
          fallbackToOriginalCart();
        }
      }
    } else {
      console.warn('🔄 SimpleVariantPool not found, using legacy fallback');
      fallbackToOriginalCart();
    }
  });

  // --- File Upload Functionality ---
  if (uploadButton && fileInput) {
    uploadButton.addEventListener('click', () => {
      fileInput.click();
    });

    fileInput.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Validate file size (max 15MB - practical limit with buffer)
      if (file.size > 15 * 1024 * 1024) {
        uploadStatus.innerHTML = `
          <div style="color: #dc3545; margin-bottom: 8px;">✗ File too large</div>
          <div style="color: #666; font-size: 12px;">
            Maximum file size is 15MB. Please compress your file or email it to us at stoveglassreplacement@gmail.com
          </div>
        `;
        uploadStatus.style.color = 'red';
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        uploadStatus.innerHTML = `
          <div style="color: #dc3545; margin-bottom: 8px;">✗ Invalid file type</div>
          <div style="color: #666; font-size: 12px;">
            Accepted formats: JPG, PNG, GIF, WebP, PDF
          </div>
        `;
        uploadStatus.style.color = 'red';
        return;
      }

      // Format file size for display
      const formatSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      };

      // Show uploading status
      uploadText.textContent = 'Uploading...';
      uploadButton.disabled = true;
      uploadStatus.innerHTML = `
        <div style="color: #2196f3; margin-bottom: 8px;">📁 Processing file...</div>
      `;
      uploadStatus.style.color = '#666';

      try {
        // Create a simple file reference (in real implementation, you'd upload to Shopify)
        const fileReference = `BRICK_DRAWING_${Date.now()}_${file.name}`;

        // Store file info for the order
        uploadedFileUrl.value = fileReference;

        // Show success with file size
        uploadStatus.innerHTML = `
          <div style="color: #28a745; margin-bottom: 8px;">✓ File ready for upload!</div>
        `;
        uploadStatus.style.color = '#28a745';
        uploadText.textContent = 'File Uploaded';
        uploadButton.disabled = false;

        // Show file info with size
        fileName.textContent = `${file.name} (${formatSize(file.size)}) ✓`;
        uploadedFileInfo.style.display = 'flex';

        // Hide upload button
        uploadButton.style.display = 'none';

        console.log('Brick file ready for upload:', file.name, formatSize(file.size));

      } catch (error) {
        console.error('Upload error:', error);
        uploadStatus.textContent = 'Upload failed. Please try again.';
        uploadStatus.style.color = 'red';
        uploadText.textContent = 'Choose File';
        uploadButton.disabled = false;
      }
    });

    if (removeFileButton) {
      removeFileButton.addEventListener('click', () => {
        // Reset file upload
        fileInput.value = '';
        uploadedFileUrl.value = '';
        uploadedFileInfo.style.display = 'none';
        uploadButton.style.display = 'inline-block';
        uploadText.textContent = 'Choose File';
        uploadStatus.textContent = '';
        uploadButton.disabled = false;
      });
    }
  }

  // --- Event Listeners ---
  const allInputs = calculator.querySelectorAll('select, input[type="number"]');
  allInputs.forEach(element => {
    element.addEventListener('input', updateCalculator);
    element.addEventListener('change', updateCalculator);
  });

  // Shape change listener for file upload visibility
  if (shapeSelect) {
    shapeSelect.addEventListener('change', updateCalculator);
  }

  // --- Initialize ---
  updateCalculator();

  // --- Expired-cart recovery: repopulate brick sizes saved on expiry.
  try {
    if (window.SGRRecovery) {
      const restored = window.SGRRecovery.consumeRestore();
      if (restored && restored.spec && restored.spec.productType === 'brick') {
        const spec = restored.spec;
        const setVal = (el, val) => {
          if (!el || val === undefined || val === null || val === '' || Number.isNaN(val)) return;
          el.value = val;
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
        };
        if (spec.thickness && thicknessSelect) {
          const opt = Array.from(thicknessSelect.options).find(o => o.value === spec.thickness || o.text.includes(spec.thickness));
          if (opt) thicknessSelect.value = opt.value;
        }
        if (spec.shape && shapeSelect) {
          const opt = Array.from(shapeSelect.options).find(o => o.value === spec.shape);
          if (opt) shapeSelect.value = spec.shape;
        }
        setVal(widthInput, spec.width || '');
        setVal(heightInput, spec.height || '');
        setVal(inputC, spec.inputC || '');
        setVal(inputD, spec.inputD || '');
        setVal(quantityInput, spec.quantity || '');
        updateCalculator();
        const notice = document.createElement('div');
        notice.setAttribute('role', 'status');
        notice.style.cssText = 'background:#e7f3ff;border:2px solid #007bff;border-radius:8px;padding:12px 14px;margin:0 0 12px 0;font-size:14px;color:#003a70;';
        notice.textContent = '✅ Your expired cart sizes were restored. Check the numbers below, then hit Add to Cart to re-add at today\'s price.';
        calculator.insertBefore(notice, calculator.firstChild);
      } else if (restored && restored.snapshot) {
        // Glass snapshot on brick page - put it back for the glass page to consume
        try { localStorage.setItem('sgr_restore_request_v1', JSON.stringify(restored.snapshot)); } catch (e) {}
      }
    }
  } catch (e) { console.warn('Brick recovery restore skipped:', e); }
}

// Initialize with multiple trigger points for maximum reliability
document.addEventListener('DOMContentLoaded', () => attemptBrickInitialization());
window.addEventListener('load', () => {
  // Double-check: if not already initialized, try again on window.load
  if (!document.getElementById('brick-calculator-wrapper')?.dataset.initialized) {
    attemptBrickInitialization();
  }
});

// Mark as initialized to prevent double-initialization
function markBrickCalculatorInitialized() {
  const calculator = document.getElementById('brick-calculator-wrapper');
  if (calculator) {
    calculator.dataset.initialized = 'true';
  }
}
